import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { getTradeBoot, receivedValue, valueTradeNow } from './helpers';
import {
  ZONE, findSkinImage, fmt, signed, pnlColor, drop,
  ZoneHead, ZoneStats, ZoneSearch, ZoneChips, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import { TradePartnerLine } from './TradePartner';
import { rowName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';
import { buildTrades } from './tradeLineage';
// Sales agreed on CSFloat but still inside Steam's trade-protection window.
// Their rows keep no sell price until the money is final, so a received item
// mid-settlement would otherwise be drawn as one you are still holding.
import { useSettling, settlesInLabel } from './settlingSales';

// Traded Zone — the Exchange Flow (Traded 2) on the Overview 7 board.
// Each trade is a gave → hub → received strip with a hard-edged P/L bar.
const FILTERS = [
  { key: 'all',      label: 'All trades' },
  { key: 'open',     label: 'Open' },
  { key: 'complete', label: 'Complete' },
];

export default function TradedTabZone({
  rows, skinsData = [], livePrices, csfloatPrices, tradePartners,
  lendingEnabled = false, onLendFromTrade,
}) {
  const settlingOf = useSettling();
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);   // the traded item whose modal is open

  // GROUPED BY TRADE, not by item given. One Steam trade can hand over seven
  // items for thirty-seven; they all share a tradeId, and listing offered rows
  // would draw that trade seven times over. buildTrades reassembles it, and the
  // other Traded views use the same function so they cannot disagree.
  const trades = useMemo(() => {
    return buildTrades(rows).map((t, idx) => {
      const gaveRows = t.gave.map(e => e.row);
      const received = t.received.map(e => e.row);
      // One valuation for the whole basket. The per-item cost slices are NOT
      // turned back into per-item profits here — see the note above
      // valueTradeNow in helpers.js for why that number was fiction.
      const worth = valueTradeNow(gaveRows, received, livePrices);
      return {
        key: t.tradeId || `legacy_${idx}`,
        // The first leg, for the actions that still act on a single row
        // (the lend offer below, which only makes sense for a one-item trade).
        realIndex: t.gave[0]?.index ?? -1,
        offeredRow: gaveRows[0],
        gave: t.gave, promised: t.promised,
        at: t.at, partner: t.partner,
        received, worth,
        cash: getTradeBoot(gaveRows[0]),
        soldCount: worth.sold,
        pnl: worth.potential,
        complete: worth.settled,
      };
    });
  }, [rows, livePrices]);

  // Both sides of every trade, priced on the day it happened and again today.
  // See TradedTab2 for why the P/L figure alone cannot answer "was that a good
  // trade" — it values what you received against a basis inherited from what
  // you gave, and never against what either was actually worth.
  const archivePoints = useMemo(() => {
    const out = [];
    for (const t of trades) {
      const date = dayOf(t.at);
      if (!date) continue;
      t.gave.forEach((g, i) => out.push({ key: `${t.key}|gave|${i}`, name: rowName(g.row), date }));
      t.received.forEach((r, i) => out.push({ key: `${t.key}|got|${i}`, name: rowName(r), date }));
    }
    return out;
  }, [trades]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  // Null unless BOTH sides price — a half-valued trade reads as a steal.
  const valueTrade = (t) => {
    // Every item on both sides, or nothing. A trade priced on four of its seven
    // given items reads as a steal that never happened.
    const gaveReads = t.gave.map((_, i) => archive.get(`${t.key}|gave|${i}`));
    if (!t.received.length || gaveReads.some(g => !g)) return null;
    const got = t.received.map((_, i) => archive.get(`${t.key}|got|${i}`));
    if (got.some(g => !g)) return null;
    const gaveThen = gaveReads.reduce((s, g) => s + g.then, 0);
    const gaveNow  = gaveReads.reduce((s, g) => s + g.now, 0);
    const gotThen  = got.reduce((s, g) => s + g.then, 0);
    const gotNow   = got.reduce((s, g) => s + g.now, 0);
    return {
      // The reading note is drawn from the first leg; they all share a date.
      gave: gaveReads[0], gaveThen, gaveNow, gotThen, gotNow,
      edgeThen: gotThen + t.cash - gaveThen,
      edgeNow:  gotNow  + t.cash - gaveNow,
    };
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return trades.filter(t => {
      if (filter === 'open' && t.complete) return false;
      if (filter === 'complete' && !t.complete) return false;
      if (q) {
        const inOffered = t.gave.some(g => g.row.skin?.value?.toLowerCase().includes(q));
        const inRecv = t.received.some(r => r.skin?.value?.toLowerCase().includes(q));
        if (!inOffered && !inRecv) return false;
      }
      return true;
    });
  }, [trades, query, filter]);

  const totals = useMemo(() => {
    const market = trades.reduce((acc, t) => {
      const v = valueTrade(t);
      if (!v) return acc;
      return {
        n: acc.n + 1, then: acc.then + v.edgeThen, now: acc.now + v.edgeNow,
        aged: acc.aged + (v.edgeNow >= v.edgeThen ? 1 : 0),
      };
    }, { n: 0, then: 0, now: 0, aged: 0 });

    return {
      // Sum of every trade's potential. Settled trades contribute real profit,
      // open ones contribute a projection, so the label has to cover both — see
      // the `settled` count that goes out with it.
      pnl:     trades.reduce((s, t) => s + t.worth.potential, 0),
      cash:    trades.reduce((s, t) => s + t.cash, 0),
      open:    trades.filter(t => !t.complete).length,
      settled: trades.filter(t => t.worth.settled).length,
      items:   trades.reduce((s, t) => s + t.received.length, 0),
      market,
    };
  }, [trades, archive]);   // eslint-disable-line react-hooks/exhaustive-deps -- valueTrade reads archive

  if (!trades.length) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="SWAP" post="BOARD" sub="Items you traded away, and what came back." />
        <ZoneEmpty>No trades yet. Mark an item as Traded and the flow shows up here.</ZoneEmpty>
      </div>
    );
  }

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="SWAP" post="BOARD"
        sub="Every trade drawn left to right: what you gave, what came back, and whether it paid off."
        stickers={[
          { text: `${trades.length} trades logged`, bg: ZONE.teal },
          totals.open > 0 && { text: `${totals.open} still open`, bg: ZONE.orange },
        ]}
      />

      <ZoneStats items={[
        { label: 'Potential from trading', value: signed(totals.pnl),
          sub: `${totals.settled} of ${trades.length} trades fully sold · the rest priced at today’s market`,
          bg: totals.pnl >= 0 ? ZONE.lime : ZONE.hot },
        { label: 'Items received', value: String(totals.items), sub: `from ${trades.length} trades`, bg: ZONE.white },
        { label: 'Cash on the side', value: signed(totals.cash),
          sub: 'taken in, less what you added', bg: ZONE.yellow },
        // Needs the price archive; hidden when no trade could be priced.
        totals.market.n > 0 && {
          label: 'Trades at market', value: signed(totals.market.now),
          sub: `today, against ${signed(totals.market.then)} on the day · ${totals.market.aged} of ${totals.market.n} aged well`,
          bg: totals.market.now >= 0 ? ZONE.lime : ZONE.pink },
      ].filter(Boolean)} />

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="zn-bar">
        <ZoneSearch value={query} onChange={setQuery} placeholder="Search trades…" />
        <ZoneChips options={FILTERS} value={filter} onChange={setFilter} />
        <span className="zn-count" style={{ marginLeft: 'auto' }}>
          {filtered.length} of {trades.length} trades
        </span>
      </div>

      {/* ── Flows ──────────────────────────────────── */}
      {filtered.length === 0 ? (
        <ZoneEmpty>No trades match your filters.</ZoneEmpty>
      ) : filtered.map((t, ti) => {
        const dateStr = t.at
          ? new Date(t.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : 'n/a';
        return (
          <motion.div key={t.key} className="zn-trade" {...drop(ti + 3, 0)}>

            <div className="zn-trade-flow">
              {/* Gave — a list, because a trade can hand over several items at
                  once. Drawn with the same card as one item so a seven-for-one
                  and a one-for-one read the same way. */}
              <div className="zn-trade-side">
                <div className="zn-trade-label">
                  Gave away{t.gave.length > 1 ? ` (${t.gave.length})` : ''}
                </div>
                <div className="zn-trade-gave-list">
                  {t.gave.map(g => {
                    const gImg = findSkinImage(skinsData, g.row.skin?.value, g.row.condition?.value);
                    return (
                      <div key={g.index} className="zn-trade-offered idm-clickable"
                        role="button" tabIndex={0} title="Open details"
                        onClick={() => setDetail({ row: g.row, img: gImg })}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ row: g.row, img: gImg }); } }}>
                        {gImg
                          ? <img src={gImg} alt="" />
                          : <div className="zn-thumb-blank" style={{ width: 62, height: 44 }} />}
                        <div style={{ minWidth: 0 }}>
                          <div className="zn-trade-name">{g.row.skin?.value}</div>
                          <div className="zn-trade-sub">
                            {g.row.condition?.value || 'n/a'} · cost ${fmt(parseFloat(g.row.buyPrice) || 0)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Hub */}
              <div className="zn-trade-hub">
                <div className="arrows">»»»</div>
                {/* Signed: you can take cash for the better item or add cash to
                    get one, and a trade that only ever showed money coming in
                    would hide half of what actually happens. */}
                {t.cash !== 0 && (
                  <div className="cash">
                    {t.cash > 0 ? `+$${fmt(t.cash)} cash` : `-$${fmt(Math.abs(t.cash))} cash`}
                  </div>
                )}
                <div className="date">{dateStr}</div>
                <TradePartnerLine row={t.offeredRow} index={tradePartners} style={{ textAlign: 'center' }} />
              </div>

              {/* Received */}
              <div className="zn-trade-side">
                <div className="zn-trade-label">
                  Received ({t.received.length})
                  <span className="zn-trade-status" style={{ background: t.complete ? ZONE.lime : ZONE.orange }}>
                    {t.complete ? 'Complete' : t.received.length === 0 ? 'Nothing logged' : `${t.soldCount}/${t.received.length} sold`}
                  </span>
                </div>
                <div className="zn-trade-recv-list">
                  {t.received.length === 0 && (
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: ZONE.mute, padding: '6px 2px' }}>
                      No received items recorded for this trade.
                      {/* Nothing came back, so Steam cannot tell a lend from a
                          gift or an off-platform sale. Offer, never guess. */}
                      {lendingEnabled && onLendFromTrade && t.realIndex !== -1 && (
                        <div style={{ marginTop: 10 }}>
                          <button type="button" className="zn-btn primary"
                            onClick={() => onLendFromTrade(t.offeredRow, t.realIndex)}>
                            This was a lend
                          </button>
                          <div style={{ marginTop: 6, lineHeight: 1.5 }}>
                            Moves it to the loan board and keeps it in your holdings, with{' '}
                            {tradePartners?.forRow?.(t.offeredRow)?.name || 'the person Steam recorded'} as the borrower.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {t.received.map((r, i) => {
                    const img = findSkinImage(skinsData, r.skin?.value, r.condition?.value);
                    // The price, never a per-item profit. Each of these carries
                    // a slice of the offered item's basis, and a slice is not a
                    // price anyone paid — see valueTradeNow in helpers.js.
                    const v = receivedValue(r, livePrices);
                    // Agreed on CSFloat, not yet out of trade protection. Until
                    // it is, the row carries no sell price and this would read
                    // as "holding".
                    const settling = !v.sold ? settlingOf(r) : null;
                    return (
                      <div key={i} className={`zn-trade-recv idm-clickable ${v.sold ? 'sold' : ''}${settling ? ' settling' : ''}`}
                        role="button" tabIndex={0} title="Open details"
                        onClick={() => setDetail({ row: r, img })}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ row: r, img }); } }}>
                        {img
                          ? <img src={img} alt="" />
                          : <div className="zn-thumb-blank" style={{ width: 62, height: 44 }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="zn-trade-name">{r.skin?.value}</div>
                          <div className="zn-trade-sub">
                            {r.condition?.value || 'n/a'} · {v.sold
                              ? 'sold'
                              : settling
                                ? `sold · ${settlesInLabel(settling.settlesAt) || 'settling'}`
                                : 'holding'}
                          </div>
                        </div>
                        <div className="zn-trade-price">
                          {/* The agreed price while settling: that is the number
                              that lands when protection ends, not today's ask. */}
                          <div className="v">
                            {settling ? `$${fmt(settling.price)}` : v.value != null ? `$${fmt(v.value)}` : 'n/a'}
                          </div>
                          <div className="k">
                            {v.sold ? 'sold for' : settling ? 'on CSFloat' : v.value != null ? 'worth now' : 'no price'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Summary strip
                Prices on the left, one profit figure on the right. The trade is
                one decision about one basket, so it gets one number — and until
                the last leg is sold that number is a projection, which is what
                "Potential profit" says out loud. */}
            <div className="zn-trade-summary">
              <div className="zn-trade-sum">
                <div className="k">Gave away</div>
                <div className="v">${fmt(t.worth.gave)}</div>
              </div>
              <div className="zn-trade-sum">
                <div className="k">{t.worth.settled ? 'Came back' : 'Worth today'}</div>
                <div className="v">
                  ${fmt(t.worth.back)}
                  {/* An unpriced leg counts as zero and would read as a loss the
                      trade never took. Say so rather than quietly understating. */}
                  {t.worth.unpriced > 0 && (
                    <span className="zn-trade-warn"> · {t.worth.unpriced} unpriced</span>
                  )}
                </div>
              </div>
              {t.cash !== 0 && (
                <div className="zn-trade-sum">
                  <div className="k">{t.cash > 0 ? 'Cash received' : 'Cash added'}</div>
                  <div className="v" style={{ color: t.cash > 0 ? ZONE.win : ZONE.lose }}>
                    {t.cash > 0 ? `+$${fmt(t.cash)}` : `-$${fmt(Math.abs(t.cash))}`}
                  </div>
                </div>
              )}
              {(() => {
                const v = valueTrade(t);
                if (!v) return null;
                return (
                  <>
                    <div className="zn-trade-sum" title={readingNote(v.gave)}>
                      <div className="k">Value on the day</div>
                      <div className="v" style={{ color: pnlColor(v.edgeThen) }}>{signed(v.edgeThen)}</div>
                    </div>
                    <div className="zn-trade-sum">
                      <div className="k">Same trade today</div>
                      <div className="v" style={{ color: pnlColor(v.edgeNow) }}>{signed(v.edgeNow)}</div>
                    </div>
                  </>
                );
              })()}
              <div className="zn-trade-sum" style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div className="k">
                  {t.worth.settled ? 'Profit from this trade' : 'Potential profit'}
                </div>
                <div className="v" style={{ color: pnlColor(t.worth.potential) }}>
                  {signed(t.worth.potential)}
                </div>
                <div className="zn-trade-note">
                  {t.worth.settled
                    ? 'every item sold · this is real money'
                    : t.worth.sold > 0
                      ? `${t.worth.sold} sold, ${t.worth.held} still held at market`
                      : 'nothing sold yet · at today’s prices'}
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}

      <ZoneFooter stickers={[
        { text: 'items you still hold are priced at live buff163 rates', bg: ZONE.purple },
        { text: `${signed(totals.pnl)} across every trade`, bg: totals.pnl >= 0 ? ZONE.lime : ZONE.hot, right: true },
      ]} />

      {detail && (
        <ItemDetailModal
          variant="zone"
          kind={parseFloat(detail.row.sellPrice) > 0 ? 'sold' : 'item'}
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          rows={rows || []}
          /* the trade overview draws a thumbnail for every item on both sides */
          skinsData={skinsData}
          /* click-through: any item in the trade chain re-centres this modal */
          onOpenRow={r => setDetail({ row: r, img: findSkinImage(skinsData, r.skin?.value, r.condition?.value) })}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
